import { Pen } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React, { useMemo } from "react";
import { Text, View, YStack } from "tamagui";
import { useApi } from "../../../core/apis";
import { useStores } from "../../../core/stores";
import { usePlanNavigation } from "../../../plans/usePlanNavigation";
import { Button } from "../../../ui/Button";
import { FormInput } from "../../../ui/FormInput";
import { Group } from "../../../ui/Group";
import { InfoRow, type InfoRowItemProps } from "../../../ui/InfoRow";
import { Label } from "../../../ui/Label";
import { LoadingCircular } from "../../../ui/LoadingCircular";
import type { BusinessCustomerModel } from "../../admin/customers/BusinessCustomerModel";

const CustomerEdit = ({
  customer,
  onSave,
}: {
  customer: BusinessCustomerModel;
  onSave: () => void;
}) => {
  const [name, setName] = React.useState(customer.data.profile.name);

  return (
    <>
      <FormInput
        label={"Name"}
        defaultValue={name}
        type="border"
        onChangeText={setName}
        placeholder={"Name"}
      />

      <Button
        onPress={() => {
          customer.updateProfile({ name });
          onSave();
        }}
      >
        Save name
      </Button>
    </>
  );
};

const CustomerRow: React.FC<{
  customer: BusinessCustomerModel;
  setEditCustomer: (customer: BusinessCustomerModel) => void;
}> = observer(({ customer, setEditCustomer }) => {
  const { navigate } = usePlanNavigation();
  const infoRowProps = useMemo<InfoRowItemProps[]>(() => {
    return [
      {
        icon: Pen,
        text: (
          <Text
            onPress={() => {
              setEditCustomer(customer);
            }}
            cursor="pointer"
          >
            Edit
          </Text>
        ),
      },
      {
        text: customer.uid,
      },
    ];
  }, [customer, setEditCustomer]);

  return (
    <YStack key={customer.uid} gap={"$medium"}>
      <Label
        onPress={() => {
          navigate("CustomerAdmin", { customerUid: customer.uid });
        }}
        cursor="pointer"
      >
        {customer.data.profile.name}
      </Label>
      <InfoRow items={infoRowProps} />
    </YStack>
  );
});

export const BusinessAdminDevCustomersList = observer(() => {
  const { businessCustomersApi } = useApi();
  const { businessCustomersStore } = useStores();
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    void businessCustomersApi.getAllCustomers().then(() => setIsLoading(false));
  }, [businessCustomersApi]);

  const customers: BusinessCustomerModel[] = businessCustomersStore.all;

  const createCustomer = React.useCallback(
    (name: string) => {
      void businessCustomersApi.add({
        profile: {
          logoUrl: null,
          name,
          theme: null,
          introText: null,
        },
        superUserInfo: {},
        applying: {},
        profileQuestions: [],
      });
    },
    [businessCustomersApi],
  );

  const [editCustomer, setEditCustomer] =
    React.useState<BusinessCustomerModel | null>(null);

  return (
    <YStack gap={"$medium"}>
      <Label>Customers ({customers.length})</Label>

      <Button
        onPress={() => {
          createCustomer(`New customer ${Date.now()}`);
        }}
      >
        Add Customer
      </Button>

      {isLoading && (
        <View alignSelf={"center"} margin={"$medium"}>
          <LoadingCircular />
        </View>
      )}

      {!!editCustomer && (
        <CustomerEdit
          key={editCustomer.uid}
          customer={editCustomer}
          onSave={() => {
            setEditCustomer(null);
          }}
        />
      )}

      <Group>
        {customers.map((customer) => (
          <CustomerRow
            key={customer.uid}
            customer={customer}
            setEditCustomer={setEditCustomer}
          />
        ))}
      </Group>
    </YStack>
  );
});
